import type {FC} from 'react';
import type {SanityImage} from '@selvklart/sanity-next-image';
import clsx from 'clsx';

import type {TextWithImageSplitSchema} from 'components/content-blocks/text-with-image-split/schema';
import {ImageFillClient} from 'components/image/client';
import {PortableText} from 'components/portable-text';

interface Props {
	section: TextWithImageSplitSchema;
	isDraft: boolean;
}

export const TextWithImageSplit: FC<Props> = ({section, isDraft}) => {
	const {alignment = 'left', content, image, title, theme} = section ?? {};

	return (
		<div className={clsx('flex', 'justify-center', 'relative')}>
			<div
				className={clsx(
					'flex',
					'w-full',
					'max-w-7xl',
					'py-24',
					'px-8',
					'gap-3',
					'flex-col',
					alignment === 'right' ? 'md:flex-row-reverse' : 'md:flex-row',
				)}
			>
				<div
					className={clsx(
						'flex-1',
						'relative',
						'overflow-hidden',
						'min-h-[450px]',
						'shadow-image',
						theme === 'red' ? 'text-red-extraLight' : 'text-blue-extraLight',
					)}
				>
					{image && image.asset && (
						<ImageFillClient image={image as SanityImage} alt={image?.altText ?? ''} />
					)}
				</div>
				<div className={clsx('flex', 'flex-1', 'relative', 'items-center')}>
					<div
						className={clsx(
							'pt-8',
							'md:pt-0',
							alignment === 'right'
								? ['md:pr-8', 'lg:pr-16', 'xl:pr-24']
								: ['md:pl-8', 'lg:pl-16', 'xl:pl-24'],
						)}
					>
						<h2
							className={clsx(
								'mt-2',
								'text-5xl',
								'font-bold',
								'tracking-tight',
								'text-primary-main',
								'sm:text-4xl',
								'font-din-condensed',
							)}
						>
							{title}
						</h2>
						{content && (
							<div
								className={clsx(
									'mt-6',
									'text-lg',
									'leading-7',
									'text-primary-light',
								)}
							>
								<PortableText blocks={content} isDraft={isDraft} />
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
